import type { Tone } from './Pill'

/**
 * One status → pill-tone vocabulary for the whole console, so a status reads the
 * same tone on every surface (the Nespresso one-system bar). Domains share the
 * lifecycle words (draft/active/closed, submitted/approved/rejected, issued/paid…);
 * this is the single source. Unknown statuses fall back to neutral.
 */
const MAP: Record<string, Tone> = {
  // generic lifecycle
  draft: 'neutral', active: 'good', closed: 'neutral', archived: 'neutral', on_hold: 'warn',
  // review / approval
  submitted: 'warn', approved: 'good', rejected: 'critical', pending: 'warn',
  in_review: 'warn', in_progress: 'warn', decided: 'good', not_initiated: 'neutral',
  // billing
  issued: 'good', paid: 'good', void: 'neutral',
  // funding drawdowns
  allocated: 'good', invoiced: 'neutral', written_off: 'warn',
  // license / deployment
  grace: 'warn', expired: 'critical',
  // misc
  open: 'warn', verified: 'good',
}

export const statusTone = (status?: string | null): Tone => (status && MAP[status]) || 'neutral'

/**
 * The DATABASE's tone vocabulary, translated into the Pill's (#1674).
 *
 * ── the defect ──────────────────────────────────────────────────────────────
 * A registered `core.status_machine` state carries a `tone`, and that word does
 * not come from `Tone`. The seeds use NINE words —
 *
 *   neutral 249 · good 175 · warn 144 · critical 79 · info 65 · brand 30
 *   · bad 19 · success 2 · muted 2
 *
 * — and `Tone` has five. Three console desks each grew their own `TONE_OF`
 * literal while converting to read the machine, and all three wrote the same
 * six-key version:
 *
 *     { good, warn, critical, brand, info: 'brand', neutral }
 *
 * so `bad`, `success` and `muted` fell through the `?? 'neutral'` and rendered
 * grey. `compliance.supplier`'s `suspended` is seeded `"tone":"bad"` (0011
 * supplier register, and again in the baseline): a suspended supplier is drawn
 * as unremarkable rather than as a problem, on the one screen whose job is to
 * show that it is one.
 *
 * ── why one function and not a shared constant ──────────────────────────────
 * Because the fallback is the part that was wrong, not the table. Three copies
 * of a `Record` each ended with its own `?? 'neutral'`, which is a decision —
 * "a word I do not know is unremarkable" — taken silently in three places. Here
 * it is taken once, and `statusToneVocabulary.test.ts` reads the seeded words
 * out of the SQL and fails when a tenth appears, so the next new word is a red
 * test rather than a grey pill.
 *
 * `info` becomes `brand` because the console has no info pill; that is a real
 * mapping, not a fallback, and it is the reason this is a translation table.
 */
const DB_TONE: Record<string, Tone> = {
  // the words `Tone` already has
  good: 'good', warn: 'warn', critical: 'critical', brand: 'brand', neutral: 'neutral',
  // and the words it does not
  info: 'brand', success: 'good', bad: 'critical', muted: 'neutral',
}

/** The pill tone for a tone word seeded on a status state or a status option. */
export const dbTone = (tone?: string | null): Tone => (tone && DB_TONE[tone]) || 'neutral'

/** The words `dbTone` knows — the census test reads this rather than the literal. */
export const dbToneWords = (): string[] => Object.keys(DB_TONE)
